import type { ReactNode } from 'react';
import {
  CRITICALITY_LABELS_UK,
  DEVICE_TYPE_LABELS_UK,
  STATUS_COLORS,
  STATUS_LABELS_UK,
  displayPortId,
  type DiagramEdge,
  type DiagramNode,
  type NodeStatus,
  type TelemetryInterface,
  type TelemetryPayload,
  type TelemetryTarget,
} from '@ukrop-nm/shared';
import { DeviceLiveLook } from '../device/DeviceLiveLook';
import {
  normalizeMac,
  resolveDeviceAppearance,
  resolveOuiVendor,
} from '../device/deviceAppearance';
import { UkropTip } from '../tooltips/DiagramTooltipContext';

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="info-row">
      <span className="info-row-label">{label}</span>
      <span className="info-row-value">{value || '—'}</span>
    </div>
  );
}

function StatusLampRow({ status }: { status?: NodeStatus }) {
  if (!status) return <Row label="Стан" value="—" />;
  return (
    <Row
      label="Стан"
      value={
        <span className="ukrop-tip-status">
          <i
            className="ukrop-status-lamp"
            style={{
              background: STATUS_COLORS[status],
              boxShadow: `0 0 0 2px ${STATUS_COLORS[status]}33`,
            }}
            aria-hidden
          />
          {STATUS_LABELS_UK[status]}
        </span>
      }
    />
  );
}

export function DeviceInfoModal({
  node,
  edge,
  status,
  evidence,
  target,
  iface,
  payload,
  onClose,
  onEdit,
  onRefreshLook,
  canEdit,
}: {
  node?: DiagramNode;
  edge?: DiagramEdge;
  status?: NodeStatus;
  evidence?: string;
  target?: TelemetryTarget | null;
  iface?: TelemetryInterface | null;
  payload?: TelemetryPayload | null;
  onClose: () => void;
  onEdit?: () => void;
  onRefreshLook?: () => void;
  canEdit?: boolean;
}) {
  if (edge && !node) {
    return (
      <div className="modal-root" role="dialog" aria-modal="true" aria-labelledby="info-title">
        <button type="button" className="modal-backdrop" aria-label="Закрити" onClick={onClose} />
        <div className="modal-panel modal-panel-wide">
          <div className="modal-header">
            <h3 id="info-title">Інформація про зʼєднання</h3>
            <button type="button" className="btn-sm" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="modal-body">
            <section className="info-section">
              <h4>Введено вручну</h4>
              <Row label="Підпис" value={edge.label} />
              <Row label="Тип зʼєднання" value={edge.connectionType} />
              <Row label="Мережа" value={edge.networkCidr || '—'} />
              <Row
                label="Порти"
                value={`${displayPortId(edge.sourcePort)}\u2192${displayPortId(edge.targetPort)}`}
              />
              <Row label="Ключ моніторингу" value={edge.monitorKey} />
              <Row label="Колір" value={edge.color} />
              <Row label="Джерело моніторингу" value={edge.monitorSource} />
              <Row label="Критичність" value={edge.criticality} />
              <Row label="Нотатки" value={edge.notes} />
            </section>
            <section className="info-section">
              <h4>Зі скрипта / симулятора</h4>
              {iface ? (
                <>
                  <Row label="Працює" value={iface.running ? 'так' : 'ні'} />
                  <Row label="Вимкнено" value={iface.disabled ? 'так' : 'ні'} />
                  <Row
                    label="Швидкість"
                    value={
                      iface.speedMbps != null
                        ? `${iface.speedMbps} Мбіт/с · ${iface.duplex ?? '—'}`
                        : '—'
                    }
                  />
                  <Row label="RX / TX (байти)" value={`${iface.rxBytes} / ${iface.txBytes}`} />
                  <Row
                    label="Помилки / скиди"
                    value={`rxE ${iface.rxErrors ?? 0} · txE ${iface.txErrors ?? 0} · drops ${(iface.rxDrops ?? 0) + (iface.txDrops ?? 0)}`}
                  />
                  <Row label="linkDowns" value={String(iface.linkDowns ?? 0)} />
                </>
              ) : (
                <p className="muted text-sm">Немає телеметрії для цього ключа моніторингу.</p>
              )}
            </section>
          </div>
          <div className="modal-footer">
            {onEdit ? (
              <button type="button" className="btn-sm" onClick={onEdit}>
                Редагувати
              </button>
            ) : null}
            <button type="button" className="btn-sm" onClick={onClose}>
              Закрити
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!node) return null;

  const inv = node.inventory ?? {};
  const mac = normalizeMac(inv.macLocal) ?? inv.macLocal?.trim() ?? '';
  const ouiVendor = resolveOuiVendor(inv.macLocal);
  const vendor = inv.vendor?.trim() || ouiVendor || '';
  const appearance = resolveDeviceAppearance({
    label: node.label,
    model: inv.model,
    vendor: inv.vendor || ouiVendor,
    macLocal: inv.macLocal,
    type: node.type,
    hasPhoto: Boolean(inv.photoDataUrl),
    appearanceId: inv.appearanceId,
  });

  return (
    <div className="modal-root" role="dialog" aria-modal="true" aria-labelledby="info-title">
      <button type="button" className="modal-backdrop" aria-label="Закрити" onClick={onClose} />
      <div className="modal-panel modal-panel-wide">
        <div className="modal-header">
          <h3 id="info-title">Інформація · {node.label}</h3>
          <button type="button" className="btn-sm" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <section className="info-look">
            <div className="info-look-frame">
              {inv.photoDataUrl ? (
                <img src={inv.photoDataUrl} alt={inv.model || node.label} className="info-look-photo" />
              ) : (
                <DeviceLiveLook
                  key={appearance.id}
                  appearanceId={appearance.id}
                  className="info-look-svg"
                />
              )}
            </div>
            <div>
              {vendor ? (
                <div className="info-look-vendor text-gray-800 dark:text-gray-100">{vendor}</div>
              ) : (
                <div className="info-look-accent text-gray-800 dark:text-gray-100" aria-hidden />
              )}
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {appearance.title}
              </div>
              <div className="text-theme-xs text-gray-500 dark:text-gray-400">
                {appearance.subtitle}
              </div>
              <div className="mt-2 text-theme-xs text-gray-400 dark:text-gray-500">
                Джерело вигляду:{' '}
                {appearance.matchedBy === 'photo'
                  ? 'власне фото'
                  : appearance.matchedBy === 'model'
                    ? 'модель'
                    : appearance.matchedBy === 'label'
                      ? 'назва'
                      : appearance.matchedBy === 'vendor'
                        ? 'MAC / виробник'
                        : appearance.matchedBy === 'cached'
                          ? 'оновлений малюнок'
                          : 'тип на схемі'}
              </div>
              {canEdit && onRefreshLook ? (
                <UkropTip text="Підібрати малюнок за назвою та моделлю">
                  <button
                    type="button"
                    className="btn-sm mt-3"
                    onClick={onRefreshLook}
                  >
                    Оновити малюнок
                  </button>
                </UkropTip>
              ) : null}
            </div>
          </section>

          <section className="info-section">
            <h4>Введено вручну</h4>
            <Row label="Тип" value={DEVICE_TYPE_LABELS_UK[node.type]} />
            <Row label="Виробник" value={vendor || '—'} />
            <Row label="Модель" value={inv.model || '—'} />
            <Row label="Назва" value={node.label} />
            <Row label="IP Address" value={inv.ipLocal || '—'} />
            <Row
              label="MAC Address"
              value={
                mac ? (
                  <code>
                    {mac}
                    {ouiVendor ? ` · ${ouiVendor}` : ''}
                  </code>
                ) : (
                  '—'
                )
              }
            />
            {node.type === 'zone' ? <Row label="Колір" value={node.color || 'за замовчуванням'} /> : null}
            {node.interfaces ? (
              <>
                <Row
                  label="Ethernet / LAN"
                  value={
                    node.interfaces.hasEthernet && (node.interfaces.ethernetCount ?? 0) > 0
                      ? String(node.interfaces.ethernetCount)
                      : '—'
                  }
                />
                <Row
                  label="SFP"
                  value={
                    node.interfaces.hasSfp && (node.interfaces.sfpCount ?? 0) > 0
                      ? String(node.interfaces.sfpCount)
                      : '—'
                  }
                />
                <Row
                  label="Wi‑Fi"
                  value={
                    node.interfaces.hasWifi && (node.interfaces.wifiCount ?? 0) > 0
                      ? `${node.interfaces.wifiCount}` +
                        [
                          node.interfaces.wifi24 ? ' · 2.4 ГГц' : null,
                          node.interfaces.wifi58 ? ' · 5.8 ГГц' : null,
                        ]
                          .filter(Boolean)
                          .join('')
                      : '—'
                  }
                />
                <Row label="WAN" value={node.interfaces.hasWan ? '1' : '—'} />
              </>
            ) : typeof node.portCount === 'number' ||
              node.type === 'switch' ||
              node.type === 'router' ? (
              <Row label="Порти" value={String(node.portCount ?? '—')} />
            ) : null}
            <Row
              label="Критичність"
              value={CRITICALITY_LABELS_UK[node.criticality] ?? node.criticality}
            />
            <StatusLampRow status={status} />
            <Row label="Ключ моніторингу" value={node.monitorKey || '—'} />
            <Row label="Розташування" value={inv.location || '—'} />
            <Row label="Опис" value={node.description || '—'} />
            <Row label="Нотатки" value={node.notes || '—'} />
            <Row label="Режим моніторингу" value={node.monitorMode || '—'} />
            <p className="mt-2 text-theme-xs text-warning-500">
              IP / MAC зберігаються лише локально в схемі і не входять у telemetry payload скрипта.
            </p>
          </section>

          <section className="info-section">
            <h4>Зі скрипта / симулятора</h4>
            <StatusLampRow status={status} />
            <Row label="Доказ (evidence)" value={evidence || '—'} />
            {target ? (
              <>
                <Row label="Статус цілі" value={target.status} />
                <Row
                  label="Затримка"
                  value={target.latencyMs != null ? `${target.latencyMs} мс` : '—'}
                />
                <Row
                  label="Втрати"
                  value={
                    target.packetLossPercent != null ? `${target.packetLossPercent}%` : '—'
                  }
                />
                <Row
                  label="Змінено"
                  value={
                    target.changedAt
                      ? new Date(target.changedAt).toLocaleString('uk-UA')
                      : '—'
                  }
                />
              </>
            ) : (
              <p className="muted text-sm">Немає телеметрії цілі для цього ключа моніторингу.</p>
            )}
            {iface ? (
              <>
                <Row label="Interface running" value={String(iface.running)} />
                <Row
                  label="Interface speed"
                  value={
                    iface.speedMbps != null
                      ? `${iface.speedMbps} Mbps · ${iface.duplex ?? '—'}`
                      : '—'
                  }
                />
              </>
            ) : null}
            {payload?.system ? (
              <>
                <Row label="Router board" value={payload.system.boardName} />
                <Row label="RouterOS" value={payload.system.routerOsVersion} />
                <Row
                  label="Site CPU / RAM"
                  value={`${payload.system.cpuLoad}% · free ${Math.round((payload.system.freeMemoryBytes / 1024 / 1024) * 10) / 10} MiB`}
                />
              </>
            ) : null}
          </section>
        </div>
        <div className="modal-footer">
          {onEdit ? (
            <button type="button" className="btn-sm" onClick={onEdit}>
              Редагувати
            </button>
          ) : null}
          <button type="button" className="btn-sm" onClick={onClose}>
            Закрити
          </button>
        </div>
      </div>
    </div>
  );
}
